import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react"
import { createPortal } from "react-dom"
import { useLocation, useNavigate } from "react-router-dom"
import { SwitchOverlayCard } from "@/components/switch-overlay-card"
import { WorkspaceTile } from "@/components/workspace-tile"
import { WorkspaceContext, useWorkspaceAccess } from "@/hooks/use-workspace"
import { switchHoldMs } from "@/lib/context-switch"
import {
  availableWorkspaces,
  rememberPath,
  resolveWorkspace,
  switchDestination,
  workspaceForPath,
  workspaceMeta,
  type WorkspaceId,
} from "@/lib/workspaces"

function safeLocalStorage(): Storage | null {
  try {
    return window.localStorage
  } catch {
    return null
  }
}

type SwitchState = { id: WorkspaceId; to?: string; phase: "leaving" | "arriving"; exiting: boolean } | null

/**
 * Owns the current workspace (derived from the URL), remembers the last page
 * in each one, and plays the switch: the same card and timings as a client
 * switch, but the page underneath navigates instead of reloading, since a
 * workspace changes nothing the API is scoped by.
 */
export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const location = useLocation()
  const navigate = useNavigate()
  const access = useWorkspaceAccess()
  const available = useMemo(() => availableWorkspaces(access), [access])

  // Shared pages (settings, search) keep the workspace that was open, so the
  // last path-owned workspace is tracked, adjusted during render on change.
  const own = workspaceForPath(location.pathname)
  const [lastOwn, setLastOwn] = useState<WorkspaceId | null>(own)
  if (own && own !== lastOwn) setLastOwn(own)
  const current = resolveWorkspace(location.pathname, lastOwn, available)

  useEffect(() => {
    rememberPath(current, location.pathname + location.search, safeLocalStorage())
  }, [current, location.pathname, location.search])

  const [switching, setSwitching] = useState<SwitchState>(null)

  const switchTo = useCallback(
    (id: WorkspaceId, to?: string) => {
      if (id === current || switching) return
      setSwitching({ id, to, phase: "leaving", exiting: false })
    },
    [current, switching]
  )

  useEffect(() => {
    if (!switching) return
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches
    if (switching.phase === "leaving") {
      // Hold the "leaving" card as long as a client switch does before its reload.
      const t = setTimeout(() => {
        navigate(switching.to ?? switchDestination(switching.id, access, safeLocalStorage()))
        setSwitching({ id: switching.id, phase: "arriving", exiting: false })
      }, switchHoldMs())
      return () => clearTimeout(t)
    }
    if (!switching.exiting) {
      const t = setTimeout(() => setSwitching((s) => (s ? { ...s, exiting: true } : s)), reduceMotion ? 250 : 700)
      return () => clearTimeout(t)
    }
    const t = setTimeout(() => setSwitching(null), 550)
    return () => clearTimeout(t)
  }, [switching, navigate, access])

  const value = useMemo(() => ({ current, available, access, switchTo }), [current, available, access, switchTo])

  return (
    <WorkspaceContext.Provider value={value}>
      {children}
      {switching &&
        createPortal(
          <SwitchOverlayCard
            phase={switching.phase}
            exiting={switching.exiting}
            title={workspaceMeta(switching.id).label}
            visual={<WorkspaceTile id={switching.id} size="lg" />}
          />,
          document.body
        )}
    </WorkspaceContext.Provider>
  )
}
